import { Info } from "@mui/icons-material";
import { Box, CircularProgress, Grid } from "@mui/material";
import { getCustomFieldByFeature } from "api/customFields";
import { fetchEventCustomFields } from "api/events/fetchEventCustomFields";
import { updateEventCustomFields } from "api/events/updateEventCustomFields";
import CustomCard from "components/CustomCard";
import CustomField from "components/CustomField";
import PanelActions from "components/PanelActions";
import { useAppContextController } from "context/AppContext";
import { featureCustomFieldSchema } from "data/featureCustomFieldSchema";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import updateEvent from "layouts/pages/events/actions/updateEvent";
import { useSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getDirtyFields } from "utils/helpers/formHelpers";
import { customPanelCard } from "../../styles";

const InterviewAdditionalInformation = () => {
  const { currentEvent, setCurrentEvent, isMobile } = useAppContextController();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch global custom fields
  const { data: customFieldsResponse, isLoading: isLoadingFields } = useQuery(
    ["fetchCustomFieldsByFeature", "Interviews"],
    () => getCustomFieldByFeature("Interviews"),
    {
      enabled: !!currentEvent?.eventUrl,
      onError: (error) => {
        enqueueSnackbar(`Error loading custom fields: ${error.message}`, { variant: "error" });
      },
    }
  );

  const customFields = useMemo(
    () => customFieldsResponse?.data || [],
    [customFieldsResponse?.data]
  );

  // Generate validation schema dynamically
  const validationSchema = useMemo(() => featureCustomFieldSchema(customFields), [customFields]);

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors, dirtyFields },
  } = useForm({
    resolver: useYupValidationResolver(validationSchema),
    defaultValues: {
      customFields: {},
    },
  });

  useEffect(() => {
    if (currentEvent?.eventUrl) {
      reset({ customFields: {} }); // Clear the form initially
    }
  }, [currentEvent?.eventUrl, reset]);

  const updateInterviewMutation = useMutation(updateEvent, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, { data }) => {
      setCurrentEvent({
        ...currentEvent,
        ...data,
        eventId: currentEvent.eventId,
        eventUrl: currentEvent.eventUrl,
      });
      enqueueSnackbar(`Interview has been updated!`, { variant: "success" });
      await queryClient.invalidateQueries("events");
    },
  });

  const editInterviewFormHandler = async (values) => {
    if (!currentEvent?.companySlug || !currentEvent?.eventId) {
      enqueueSnackbar("Cannot Update Interview -- No Interview ID!", { variant: "error" });
      return;
    }
    const modifiedFields = getDirtyFields(values, dirtyFields);
    if (modifiedFields?.eventEndTime) {
      modifiedFields.eventEndTime = new Date(modifiedFields.eventEndTime).toISOString();
    }
    if (modifiedFields?.eventDate) {
      modifiedFields.eventDate = new Date(modifiedFields.eventDate).toISOString();
    }
    await updateInterviewMutation.mutateAsync({
      companySlug: currentEvent?.companySlug,
      eventId: currentEvent?._id,
      data: {
        ...modifiedFields,
      },
    });
  };

  const cancelChangesHandler = () => {
    const url = currentEvent?.eventUrl;
    reset(currentEvent);
    enqueueSnackbar("Changes have been abandoned!", { variant: "success" });
  };

  useEffect(() => {
    reset(currentEvent);
  }, [currentEvent]);

  return (
    <form onSubmit={handleSubmit(editInterviewFormHandler)}>
      <CustomCard
        icon={<Info color="white" />}
        cardTitle={`Additional Information: ${currentEvent?.eventName}`}
        cardActions={isDirty && <PanelActions onCancel={cancelChangesHandler} />}
        {...customPanelCard(isMobile, "primary")}
      >
        {isLoadingFields ? (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <Grid container spacing={4} p={3}>
            {customFields
              .sort((a, b) => a.order - b.order)
              .filter((field) => field.isVisible)
              .map((field) => (
                <Grid item xs={12} md={6} key={field.field}>
                  <CustomField
                    field={field}
                    control={control}
                    error={errors?.[`customFields.${field.field}`]?.message}
                  />
                </Grid>
              ))}
          </Grid>
        )}
      </CustomCard>
    </form>
  );
};

export default InterviewAdditionalInformation;
